import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    this.#autoNestCartItemsOnLoad();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    const lineItemRow = this.refs.cartItemRows[line - 1];
    if (!lineItemRow) return;

    const parentKey = lineItemRow.dataset.key;
    const nestedRows = parentKey
      ? this.refs.cartItemRows.filter((row) => row.dataset.parentKey === parentKey)
      : [];
    const nestedLines = nestedRows
      .map((row) => row.dataset.line)
      .filter((lineNumber) => typeof lineNumber === 'string' && lineNumber.length > 0)
      .map((l) => parseInt(/** @type {string} */ (l), 10));

    if (nestedLines.length > 0) {
      this.#updateQuantityWithNested(line, nestedLines, quantity);
    } else {
      this.updateQuantity({
        line,
        quantity,
        action: 'change',
      });
    }

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates parent line and all nested lines to the same quantity, then morphs once.
   * @param {number} parentLine - The parent line number (1-based).
   * @param {number[]} nestedLines - Nested line numbers (1-based).
   * @param {number} quantity - The quantity to set.
   */
  async #updateQuantityWithNested(parentLine, nestedLines, quantity) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker('change:user-action');
    this.#disableCartItems();
    this.refs.cartTotal?.shimmer();

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const allLines = [parentLine, ...nestedLines];
    let lastParsedResponse = null;

    try {
      for (const line of allLines) {
        const body = JSON.stringify({
          line,
          quantity,
          sections: Array.from(sectionsToUpdate).join(','),
          sections_url: window.location.pathname,
        });
        const response = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));
        const responseText = await response.text();
        lastParsedResponse = JSON.parse(responseText);

        if (lastParsedResponse.errors) {
          this.#handleCartError(line, lastParsedResponse);
          return;
        }
      }

      if (lastParsedResponse?.sections?.[this.sectionId]) {
        const newSectionHTML = new DOMParser().parseFromString(
          lastParsedResponse.sections[this.sectionId],
          'text/html'
        );
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.#updateQuantitySelectors(lastParsedResponse);
        this.dispatchEvent(
          new CartUpdateEvent(lastParsedResponse, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: lastParsedResponse.sections,
          })
        );
        morphSection(this.sectionId, lastParsedResponse.sections[this.sectionId]);
        this.#updateCartQuantitySelectorButtonStates();
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.#enableCartItems();
      resetShimmer(this);
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Ensures the cart items for physical products are nested under their
   * corresponding digital (subscription) parent items when the cart page loads.
   *
   * - Shows a lightweight loading overlay while the operation runs
   * - Leaves already nested items untouched
   * - For non‑nested physical items, re-adds them as children of a digital item
   */
  async #autoNestCartItemsOnLoad() {
    // Only run once per page.
    if (/** @type {any} */ (window).__cartAutoNestInitialized) return;

    /** @type {any} */ (window).__cartAutoNestInitialized = true;

    try {
      const cartResponse = await fetch('/cart.js', { credentials: 'same-origin' });
      /** @type {{ items?: Array<any>, item_count?: number }} */
      const cart = await cartResponse.json();

      const items = cart.items || [];

      // Already nested = has parent_relationship (or parent_id); leave these untouched.
      /** @param {any} item */
      const isNested = (item) => !!(item?.parent_relationship ?? item?.parent_id);

      // Potential parents: top-level items that do not require shipping (any such item can be parent).
      const potentialParents = items.filter(
        (item) =>
          item &&
          item.requires_shipping === false &&
          !isNested(item)
      );

      // Items that require shipping and are not yet nested — they should be children of another product.
      const physicalChildren = items.filter(
        (item) => item && item.requires_shipping === true && !isNested(item)
      );

      // Detect inconsistent "non-pair" state: shipping items with no parent to nest under.
      if (physicalChildren.length > 0 && potentialParents.length === 0) {
        console.warn(
          '[cart-auto-nest] Inconsistent cart:',
          physicalChildren.length,
          'shipping item(s) but no non-shipping parent. Cannot nest. Add a subscription/digital product first, or ignore if this cart is intentional.'
        );
        return;
      }

      if (potentialParents.length === 0 || physicalChildren.length === 0) {
        return;
      }

      // Pair all un-nested shipping items with the first non-shipping (potential parent) item.
      const parent = potentialParents[0];
      const parentVariantId = parent.variant_id;
      const sellingPlanId = parent.selling_plan_allocation?.selling_plan?.id;

      if (!parentVariantId) {
        console.log('[cart-auto-nest] Parent has no variant_id, aborting');
        return;
      }

      // Use original parent quantity; parent and all children must have the exact same quantity.
      const bundleQuantity = Math.max(1, Number(parent.quantity) || 1);

      // Build a NEW parent+children bundle to add to cart in one request,
      // so the children are born nested under the new parent.
      /** @type {Array<any>} */
      const itemsToAdd = [];

      itemsToAdd.push({
        id: parentVariantId,
        quantity: bundleQuantity,
        ...(sellingPlanId ? { selling_plan: sellingPlanId } : {}),
      });

      for (const child of physicalChildren) {
        itemsToAdd.push({
          id: child.variant_id,
          quantity: bundleQuantity,
          parent_id: parentVariantId,
        });
      }

      if (itemsToAdd.length <= 1) {
        console.log('[cart-auto-nest] No valid bundle constructed, aborting');
        return;
      }

      // Remove the original parent and children first so we don't add up quantities
      // (e.g. same variant would merge and double the parent quantity).
      const linesToRemove = [
        parent,
        ...physicalChildren,
      ].filter((item) => item && item.key);

      const changePromises = linesToRemove.map((item) => {
        const body = JSON.stringify({
          id: item.key,
          quantity: 0,
          sections: this.sectionId,
          sections_url: window.location.pathname,
        });

        return fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));
      });

      await Promise.all(changePromises);

      // Build restore payload: same variants without parent_id, in case add fails.
      const itemsToRestore = [
        { id: parentVariantId, quantity: bundleQuantity, ...(parent.selling_plan_allocation?.selling_plan?.id ? { selling_plan: parent.selling_plan_allocation.selling_plan.id } : {}) },
        ...physicalChildren.map((c) => ({ id: c.variant_id, quantity: bundleQuantity })),
      ];

      const tryAddBundle = () =>
        fetch(Theme.routes.cart_add_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ items: itemsToAdd }),
          credentials: 'same-origin',
        }).then((r) => r.json());

      let addResult = await tryAddBundle();

      if (addResult.status) {
        addResult = await tryAddBundle();
      }

      if (addResult.status) {
        console.warn('[cart-auto-nest] Bundle add failed after removing originals; restoring cart without nesting.', addResult);
        const restoreResponse = await fetch(Theme.routes.cart_add_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ items: itemsToRestore }),
          credentials: 'same-origin',
        });
        const restoreResult = await restoreResponse.json();
        if (restoreResult.status) {
          console.error('[cart-auto-nest] Restore failed; cart may be missing items.', restoreResult);
        }
        await this.#refreshCartSection();
        return;
      }

      await this.#refreshCartSection();
    } catch (error) {
      console.error('Error auto-nesting cart items:', error);
    }
  }

  /**
   * Fetches the current cart section and cart state, morphs the section into the DOM, and dispatches CartUpdateEvent.
   */
  async #refreshCartSection() {
    const sectionsResponse = await fetch(`/?sections=${this.sectionId}`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    const sections = await sectionsResponse.json();

    const refreshedCartResponse = await fetch('/cart.js', { credentials: 'same-origin' });
    const refreshedCart = await refreshedCartResponse.json();
    const itemCount = typeof refreshedCart.item_count === 'number' ? refreshedCart.item_count : 0;

    if (sections[this.sectionId]) {
      morphSection(this.sectionId, sections[this.sectionId]);
      this.dispatchEvent(
        new CartUpdateEvent(refreshedCart, this.sectionId, {
          itemCount,
          source: 'cart-items-component',
          sections,
        })
      );
      this.#updateCartQuantitySelectorButtonStates();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
